import { useCallback, useMemo } from 'react';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../flags';
import { PreferenceKey, usePreferences } from './usePreferences';

/**
 * Hook for managing debug preferences for save/publish operations.
 * Only active when the ngw_debug feature flag is enabled.
 */
export function useDebugPreferences() {
  const { hasFeature } = useFeatureFlags();
  const { usePreference } = usePreferences();

  // Check if the debug feature flag is enabled
  const isDebugFlagEnabled = hasFeature(FeatureFlags.DEBUG);

  // Individual debug preferences
  const [debugPublish, setDebugPublish] = usePreference(
    PreferenceKey.DEBUG_PUBLISH_ENABLED,
    false,
  );
  const [debugSaves, setDebugSaves] = usePreference(
    PreferenceKey.DEBUG_SAVES_ENABLED,
    false,
  );

  // Parent checkbox state - checked if any child is checked
  const isDebugSaveEnabled = useMemo(
    () => debugPublish || debugSaves,
    [debugPublish, debugSaves],
  );

  // Toggle parent checkbox - toggles both children
  const toggleDebugSave = useCallback(
    (enabled: boolean) => {
      setDebugPublish(enabled);
      setDebugSaves(enabled);
    },
    [setDebugPublish, setDebugSaves],
  );

  // Handler for publish checkbox change
  const handleDebugPublishChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setDebugPublish(event.target.checked);
    },
    [setDebugPublish],
  );

  // Handler for saves checkbox change
  const handleDebugSavesChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setDebugSaves(event.target.checked);
    },
    [setDebugSaves],
  );

  // Handler for parent checkbox change
  const handleDebugSaveChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      toggleDebugSave(event.target.checked);
    },
    [toggleDebugSave],
  );

  return {
    // Feature flag check
    isDebugFlagEnabled,

    // Parent state
    isDebugSaveEnabled,
    handleDebugSaveChange,
    toggleDebugSave,

    // Child states
    debugPublish,
    debugSaves,
    setDebugPublish,
    setDebugSaves,
    handleDebugPublishChange,
    handleDebugSavesChange,
  };
}
